/**
 * Timezone Resolver Module
 *
 * Validates and resolves the configured IANA timezone from the
 * NEXT_PUBLIC_OPS_PORTAL_TIMEZONE environment variable.
 * Falls back to "GMT" if the value is empty, unset, or invalid.
 */

const FALLBACK_TIMEZONE = 'GMT';

/**
 * Validates an IANA timezone string using Intl.DateTimeFormat.
 * Returns true if the timezone is recognized, false otherwise.
 */
export function isValidIANATimezone(tz: string): boolean {
  try {
    Intl.DateTimeFormat('en', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/**
 * Resolves the configured timezone from the environment variable.
 * Falls back to "GMT" if empty, unset, or invalid.
 * Logs a console warning for invalid values.
 */
export function resolveTimezone(): string {
  const envValue = process.env.NEXT_PUBLIC_OPS_PORTAL_TIMEZONE;

  if (!envValue?.trim()) {
    return FALLBACK_TIMEZONE;
  }

  const trimmed = envValue.trim();

  if (isValidIANATimezone(trimmed)) {
    return trimmed;
  }

  console.warn(
    `[timezoneResolver] Invalid IANA timezone: "${trimmed}". Falling back to "${FALLBACK_TIMEZONE}".`
  );
  return FALLBACK_TIMEZONE;
}

/**
 * The resolved timezone value (computed once at module load).
 * Consumers import this constant.
 */
export const RESOLVED_TIMEZONE: string = resolveTimezone();
